import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Trophy, Calendar, Zap, Lock, Users, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import CountdownTimer from '../components/CountdownTimer';

const WelcomeJan8 = () => {
  const navigate = useNavigate();
  const [isLaunchTime, setIsLaunchTime] = useState(false);
  const [user, setUser] = useState(null);

  // Target launch date: January 8th, 2026 @ 9:00 AM EST
  const LAUNCH_DATE = new Date('2026-01-08T09:00:00-05:00');

  useEffect(() => {
    // Check if user is logged in
    const userStr = localStorage.getItem('academy_user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Error parsing user data');
      }
    }

    // Check if it's launch time
    const checkLaunchTime = () => {
      const now = new Date();
      if (now >= LAUNCH_DATE) {
        setIsLaunchTime(true);
      }
    };

    checkLaunchTime();
    const interval = setInterval(checkLaunchTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // If it's launch time, redirect to dashboard
  useEffect(() => {
    if (isLaunchTime) {
      navigate('/academy/high-performance');
    }
  }, [isLaunchTime, navigate]);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-600/10 via-black to-black" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl animate-pulse delay-1000" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: 'linear-gradient(rgba(249,115,22,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.5) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16">
        {/* Lock Icon with Pulse */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-orange-500/30 rounded-full blur-xl animate-pulse"></div>
          <div className="relative w-28 h-28 bg-gradient-to-br from-orange-500/30 to-orange-600/20 rounded-full flex items-center justify-center border-2 border-orange-500/50">
            <Lock className="w-14 h-14 text-orange-500" />
          </div>
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center animate-bounce">
            <Sparkles className="w-3 h-3 text-black" />
          </div>
        </div>

        {/* Welcome Badge */}
        <div className="mb-4">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-500 text-sm font-bold tracking-wider">
            <Users className="w-4 h-4" />
            YOU'RE ON THE ROSTER
          </span>
        </div>

        {/* Welcome Message */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-center mb-4 tracking-tight">
          WELCOME TO THE <span className="text-orange-500 relative">
            COMBINE
            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-orange-500/50 rounded-full"></div>
          </span>
        </h1>
        
        {user && (
          <p className="text-xl md:text-2xl text-gray-300 mb-2 font-medium">
            <span className="text-orange-500">{user.name || user.email}</span>, you're officially cleared.
          </p>
        )}

        <p className="text-gray-500 text-center max-w-2xl mb-10 text-lg">
          Your seat is secured. The Combine doors open on{' '}
          <span className="text-orange-500 font-bold">Thursday, January 8th, 2026 at 9:00 AM EST</span>.
          <br />
          <span className="text-gray-600 text-base">Until then, this is your Waiting Room.</span>
        </p>

        {/* Countdown Timer */}
        <div className="w-full max-w-3xl mb-16">
          <CountdownTimer 
            targetDate="2026-01-08T09:00:00-05:00" 
            label="COMBINE OPENS IN" 
          />
        </div>

        {/* What's Coming Section */}
        <div className="w-full max-w-4xl">
          <h2 className="text-xl font-black text-center mb-8 text-orange-500 tracking-widest">
            WHAT'S WAITING FOR YOU
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/40 border border-gray-800 hover:border-orange-500/30 rounded-xl p-6 text-center transition-all duration-300 hover:scale-105 group">
              <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors">
                <Trophy className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="font-bold mb-2 text-white group-hover:text-orange-500 transition-colors">High-Performance Dashboard</h3>
              <p className="text-gray-500 text-sm">Track your progress and dominate your 30-day sprint</p>
            </div>
            
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/40 border border-gray-800 hover:border-orange-500/30 rounded-xl p-6 text-center transition-all duration-300 hover:scale-105 group">
              <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors">
                <Zap className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="font-bold mb-2 text-white group-hover:text-orange-500 transition-colors">Coach's Corner</h3>
              <p className="text-gray-500 text-sm">Exclusive video content and training materials</p>
            </div>
            
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/40 border border-gray-800 hover:border-orange-500/30 rounded-xl p-6 text-center transition-all duration-300 hover:scale-105 group">
              <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors">
                <Calendar className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="font-bold mb-2 text-white group-hover:text-orange-500 transition-colors">The Vault</h3>
              <p className="text-gray-500 text-sm">Playbooks, worksheets, and elite resources</p>
            </div>
          </div>
        </div>

        {/* While You Wait Section */}
        <div className="w-full max-w-2xl mt-12">
          <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-xl p-6">
            <h3 className="text-lg font-bold text-orange-500 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              WHILE YOU WAIT
            </h3>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>Check your email for your personalized <span className="text-orange-500 font-semibold">Letter of Intent</span></span>
              </li>
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>Download and sign your commitment document</span>
              </li>
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>Prepare for your 30-Day Pivot sprint</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 text-sm mb-4">
            Questions? Contact us at{' '}
            <a href="mailto:support@prsvr.store" className="text-orange-500 hover:underline font-medium">
              support@prsvr.store
            </a>
          </p>
        </div>

        {/* Footer Note */}
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <p className="text-gray-700 text-xs tracking-widest uppercase">
            The first move was the pivot — the next move is the work.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeJan8;
